import { Fragment, ReactNode, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { ComboboxData, Option } from "components/ui/combobox/types";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { useDebouncedValue } from "lib/debounce";
import type { SearchLeadsRequest } from "lib/generated/app-service-gql/graphql";
import { useLeadsSimple } from "lib/hooks/api/leads/useLeadsSimple";
import { routes } from "lib/routes";
import { LeadSortBy, LeadSortOrder } from "lib/types/graphQLEnums";
import { ComboboxProps } from "../../combobox/ComboBox";
import { EntityOption } from "./ComboboxOption";
import { EmptyComboboxTrigger } from "./EmptyComboboxTrigger";
import { SelectedEntityCard } from "./SelectedEntityCard";

export type RelatedLeadComboboxProps<TFieldValues extends FieldValues = FieldValues> = Required<
  Pick<ComboboxProps<TFieldValues>, "control" | "name">
> & {
  defaultLeadRequest?: SearchLeadsRequest;
  selectedLead?: { id: string; name: string } | null;
};

function ComboboxContentComponent(props: {
  combobox: ComboboxData;
  setSearchText: (text: string) => void;
  disabled?: boolean;
  loading: boolean;
  trigger?: ReactNode;
}) {
  const { trigger, combobox, setSearchText, disabled, loading } = props;
  return (
    <ComboboxContent
      open={combobox.open}
      setOpen={(open: boolean) => {
        combobox.setOpen(open);
      }}
      trigger={trigger ? trigger : <div />}
    >
      <ComboboxInput
        placeholder={"Search leads..."}
        filterValue={combobox.filterValue}
        setFilterValue={(value) => {
          combobox.setFilterValue(value);
          setSearchText(value);
        }}
        open={combobox.open}
        setOpen={combobox.setOpen}
        value={[]}
        items={combobox.items}
        multiple={combobox.multiple}
        inputRef={combobox.inputRef}
        inputElementRef={combobox.inputElementRef}
        disabled={disabled}
      />
      <ComboboxEmpty
        filteredItems={combobox.filteredItems}
        filterValue={combobox.filterValue}
        disableAddingOptions
      >
        {loading ? <SkeletonText width="100%" /> : "No leads found."}
      </ComboboxEmpty>

      <ListScrollArea>
        <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
          <ComboboxCollection options={combobox.filteredItems as Option[]}>
            {(option: Option) => {
              return (
                <ComboboxItem
                  key={String(option.value)}
                  option={option}
                  value={combobox.value}
                  selectItem={combobox.selectItem}
                  multiple={combobox.multiple}
                  highlightedIndex={combobox.highlightedIndex}
                  filteredItems={combobox.filteredItems}
                >
                  <EntityOption label={option.label} />
                </ComboboxItem>
              );
            }}
          </ComboboxCollection>
        </ComboboxList>
      </ListScrollArea>
    </ComboboxContent>
  );
}

export function RelatedLeadCombobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  defaultLeadRequest,
  selectedLead,
}: RelatedLeadComboboxProps<TFieldValues>) {
  const { field } = useController({ control, name });
  const [searchText, setSearchText] = useState("");
  const [debouncedText] = useDebouncedValue(searchText, 300);
  const [clickedLeadId, setClickedLeadId] = useState<string | null>(null);

  const { data: leads, isLoading } = useLeadsSimple(
    {
      ...defaultLeadRequest,
      searchText: debouncedText || undefined,
      orderBy: LeadSortBy.Name,
      order: LeadSortOrder.Asc,
    },
    {
      page: 1,
      pageSize: 100,
    },
  );

  const getSelectedLead = (lead: { id: string; name: string }) => {
    if (selectedLead && selectedLead.id === lead.id) {
      return selectedLead;
    }
    if (!lead || !leads?.results) return null;
    return leads.results.find((l) => l.id === lead.id) || null;
  };

  const getOptions = (): Option[] => {
    const options: Option[] = leads?.results
      ? leads.results.map((lead) => ({
          label: lead.name,
          value: lead.id,
        }))
      : [];

    const unassignedOption: Option = {
      label: "Unassigned",
      value: "",
    };
    options.unshift(unassignedOption);

    return options;
  };

  const options = getOptions();
  const arrayValue = field.value === null ? [""] : field.value ? [field.value] : [];

  return (
    <Combobox
      items={options}
      value={arrayValue}
      onChange={(values) => {
        const newValue = values.length > 0 ? values[0] : null;
        field.onChange(newValue === "" ? null : newValue);
      }}
      multiple={false}
    >
      {(combobox) => {
        const selectedValue = combobox.value[0];
        const selectedLead =
          selectedValue && selectedValue !== ""
            ? getSelectedLead({ id: String(selectedValue), name: "" })
            : null;
        return (
          <PropertyInnerContainer>
            <Flex column gap={8} width="100%" minWidth={0}>
              {selectedLead ? (
                <Fragment key={selectedLead.id}>
                  <SelectedEntityCard
                    entity={selectedLead}
                    dataType="lead"
                    combobox={combobox}
                    setEntityClickerId={setClickedLeadId}
                    setIsOpen={null}
                    clickedEntityId={clickedLeadId}
                    linkButtonProps={{ href: `${routes.leads}/${selectedLead.id}` }}
                  />
                  <ComboboxContentComponent
                    combobox={combobox}
                    setSearchText={setSearchText}
                    disabled={false}
                    loading={isLoading}
                  />
                </Fragment>
              ) : (
                <ComboboxContentComponent
                  combobox={combobox}
                  setSearchText={setSearchText}
                  disabled={isLoading}
                  loading={isLoading}
                  trigger={
                    <EmptyComboboxTrigger
                      label="Related Lead"
                      disabled={isLoading}
                      onClick={() => {
                        combobox.setOpen(true);
                      }}
                    />
                  }
                />
              )}
            </Flex>
          </PropertyInnerContainer>
        );
      }}
    </Combobox>
  );
}

const PropertyInnerContainer = styled.div({
  minWidth: 0,
  width: "100%",
});

const ListScrollArea = styled.div({
  maxHeight: 390,
  overflowY: "auto",
});
